"use client";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

type Suggestion = { title: string; url: string; key: string; snippet?: string; source?: string; description?: string };

export default function AutocompleteSearch() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const initial = (searchParams.get('q') || searchParams.get('query') || '').trim();
    const [query, setQuery] = useState(initial);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const abortRef = useRef<AbortController | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [focused, setFocused] = useState(false);
    const placeholders = [
        'rental license',
        'real estate investing',
        'property management',
        'construction projects',
        'Philadelphia rentals'
    ];
    const [phIndex, setPhIndex] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setPhIndex((i) => (i + 1) % placeholders.length), 3000);
        return () => clearInterval(t);
    }, [placeholders.length]);

    const debouncedQuery = useDebounce(query, 200);

    useEffect(() => {
        if (!debouncedQuery) {
            setSuggestions([]);
            setOpen(false);
            return;
        }
        setLoading(true);
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        fetch(`/api/search?q=${encodeURIComponent(debouncedQuery)}&limit=8`, { signal: ac.signal })
            .then(r => r.json())
            .then(data => {
                setSuggestions(data.suggestions || []);
                setOpen((data.suggestions || []).length > 0 && focused);
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [debouncedQuery, focused]);

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) return;
        router.push(`/search?q=${encodeURIComponent(q)}&page=1`);
        setOpen(false);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' && suggestions.length > 0) {
            // focus first suggestion
            const el = document.getElementById('suggest-0');
            el?.focus();
            e.preventDefault();
        }
    };

    return (
        <div
            ref={rootRef}
            className="relative w-full max-w-2xl"
            aria-busy={loading}
            onFocusCapture={() => setFocused(true)}
            onBlurCapture={(e) => {
                const next = e.relatedTarget as Node | null;
                if (!e.currentTarget.contains(next)) {
                    setFocused(false);
                    setOpen(false);
                }
            }}
        >
            <form onSubmit={onSubmit} className="flex items-center" role="search">
                <div className="relative w-full">
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onFocus={() => setFocused(true)}
                        onKeyDown={onKeyDown}
                        placeholder={''}
                        className="w-full rounded-md border border-purple-900 bg-[var(--background2-color)] px-4 pr-10 py-2 text-sm shadow-sm focus:border-purple-800 focus:outline-none"
                        aria-label="Search"
                        type="search"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={open}
                        aria-controls="search-suggestions"
                    />
                    {query.trim() === '' && (
                        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                            {'Search for '}
                            <span className="uppercase font-semibold">{placeholders[phIndex]}</span>
                        </span>
                    )}
                    <button
                        type="submit"
                        aria-label="Submit search"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-500 focus:outline-none"
                    >
                        <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" strokeWidth={3} />
                    </button>
                </div>
            </form>
            {open && suggestions.length > 0 && (
                <ul id="search-suggestions" className="absolute z-20 mt-2 w-full overflow-hidden rounded-md border border-purple-900 bg-[var(--background2-color)] shadow-xl" role="listbox">
                    {suggestions.map((s, i) => (
                        <li key={s.key}>
                            <button
                                id={`suggest-${i}`}
                                className="group flex w-full flex-col items-start gap-1 px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-100"
                                onClick={() => {
                                    router.push(s.url);
                                    setOpen(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'ArrowUp' && i === 0) {
                                        inputRef.current?.focus();
                                        e.preventDefault();
                                    } else if (e.key === 'ArrowDown') {
                                        const next = document.getElementById(`suggest-${i + 1}`);
                                        next?.focus();
                                        e.preventDefault();
                                    }
                                }}
                                role="option"
                            >
                                <div className="flex w-full items-center justify-between gap-2">
                                    <span className="truncate text-md text-white group-hover:text-purple-900 group-hover:font-semibold transition-colors">{highlightText(s.title || s.key, query)}</span>
                                    <span className="flex-none truncate text-xs text-purple-500">{(s.url || '').replace(/^https?:\/\/[^/]+/i, '')}</span>
                                </div>
                                {s.snippet && (
                                    <>
                                        <div className="mt-1 w-full text-sm text-[var(--body-1-color)] group-hover:text-purple-800">
                                            {highlightText(s.snippet, query)}
                                        </div>
                                    </>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {loading && (
                <div className="absolute right-8 top-3 text-xs text-purple-400" role="status" aria-live="polite">Searching…</div>
            )}
        </div>
    );
}

function highlightText(text: string, q: string) {
    if (!text || !q) return text;
    const esc = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${esc})`, 'ig'));
    return (
        <>
            {parts.map((p, idx) =>
                p.toLowerCase() === q.toLowerCase() ? (
                    <span key={idx} className="text-purple-900 font-semibold">{p}</span>
                ) : (
                    <span key={idx}>{p}</span>
                )
            )}
        </>
    );
}

function useDebounce<T>(value: T, ms = 200): T {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
}

// removed: previously extracted exact match; now snippet highlights the keyword inline
